import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { theme, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { DimensionHeading } from "../../components/Drawing";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../../components/RecordView";
import { NextSteps, type NextStepItem } from "../../components/NextSteps";
import { tableHeadRow, fmtDateOnly, fmtDateTime, todayInput, toDateInput, fromInput, ModalOverlay, ModalHeader, Field, input, textarea, secondaryBtn, dangerBtn, StatusPill, type Option, type SyncResult } from "./shared";
import { SyncNotice } from "./IncidentsTab";

// Claims (and notifications of circumstances) made under a cyber insurance policy:
// /resilience/insurance/:policyId/claims (backend/src/routes/resilienceInsights.routes.ts).
// A claim may name the incident it arises from; the incident must be this organisation's.

export const CLAIM_STATUSES = ["notified", "under_review", "accepted", "declined", "paid", "closed"] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];
export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  notified: "Notified", under_review: "Under review", accepted: "Accepted", declined: "Declined", paid: "Paid", closed: "Closed",
};

export interface InsuranceClaim {
  id: string;
  policyId: string;
  incidentId: string | null;
  notifiedAt: string;
  status: ClaimStatus;
  reference: string | null;
  amountClaimed: number | null;
  amountPaid: number | null;
  closedAt: string | null;
  notes: string | null;
  open: boolean;
  incidentRef: string | null;
  incident: { id: string; displayId: number | null; title: string } | null;
  policy: { id: string; insurer: string; policyNumber: string | null; currency: string };
  /** The incident's insurer notification this claim is the notice of (backend services/recordSync.ts). */
  notificationId?: string | null;
  notification?: { id: string; status: string; sentAt: string | null; reference: string | null } | null;
}

const STATUS_TONE: Record<ClaimStatus, { bg: string; color: string }> = {
  notified: { bg: theme.color.accentBg, color: theme.color.accentText },
  under_review: { bg: theme.color.warningBg, color: theme.color.warningText },
  accepted: { bg: theme.color.successBg, color: theme.color.successText },
  declined: { bg: theme.color.dangerBg, color: theme.color.dangerText },
  paid: { bg: theme.color.successBg, color: theme.color.successText },
  closed: { bg: theme.color.panelAlt, color: theme.color.muted },
};

export function ClaimStatusPill({ status }: { status: ClaimStatus }) {
  const s = STATUS_TONE[status] ?? STATUS_TONE.closed;
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{CLAIM_STATUS_LABELS[status] ?? status}</span>;
}

/** What a claim record needs to stand up to the insurer and the auditor: the insurer's reference, the incident, the amount and, once settled, the outcome. */
function claimSteps(c: InsuranceClaim): NextStepItem[] {
  if (c.status === "closed") return [];
  const settled = c.status === "paid" || c.status === "declined";
  return [
    { done: !!c.reference?.trim(), label: "Record the insurer's claim reference" },
    { done: !!c.incidentId, label: "Link the incident the claim arises from" },
    { done: c.amountClaimed != null, label: "Record the amount claimed" },
    ...(c.status === "paid" ? [{ done: c.amountPaid != null, label: "Record the amount paid" }] : []),
    ...(settled ? [{ done: !!c.closedAt, label: "Record the date it was settled" }] : []),
  ];
}

const money = (v: number | null, ccy: string) => (v == null ? "—" : `${ccy} ${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`);

export function PolicyClaims({ policyId, currency, claims, canEdit, onChanged }: { policyId: string; currency: string; claims: InsuranceClaim[]; canEdit: boolean; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState<{ claim: InsuranceClaim | null } | null>(null);
  // The claim open in its read-only view.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // What the last save also recorded on the incident's insurer notification, or could not.
  const [synced, setSynced] = useState<SyncResult | null>(null);
  const viewing = viewingId ? claims.find((c) => c.id === viewingId) ?? null : null;

  async function remove(c: InsuranceClaim) {
    if (!window.confirm(`Delete the claim record${c.reference ? ` ${c.reference}` : ""}?`)) return;
    try { await api.del(`/resilience/insurance/${policyId}/claims/${c.id}`); setViewingId(null); await onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "Could not delete the claim")); }
  }

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <DimensionHeading as="h3" count={claims.length} style={{ margin: 0, flex: 1, minWidth: 0 }}>Claims and notifications</DimensionHeading>
        {canEdit && <button type="button" style={secondaryBtn} onClick={() => setEditing({ claim: null })}><Plus size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Record a claim</button>}
      </div>
      {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
      {synced && !viewing && <SyncNotice sync={synced} onDismiss={() => setSynced(null)} />}
      {claims.length === 0 ? (
        <div style={text.hint}>No claims or notifications of circumstances recorded against this policy.</div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid var(--c-border)", borderRadius: 3 }}>
          <table style={{ ...text.hint, width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={tableHeadRow}>
              {["Notified", "Reference", "Incident", "Status", "Claimed", "Paid"].map((h, i) => <th key={i} style={{ padding: "7px 10px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {claims.map((c) => (
                <tr key={c.id} onClick={(ev) => { if (!(ev.target instanceof Element && ev.target.closest("a"))) setViewingId(c.id); }} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap", color: theme.color.text }}>{fmtDateOnly(c.notifiedAt)}</td>
                  <td style={{ padding: "7px 10px", color: theme.color.muted }}>{c.reference ? <span style={text.ref}>{c.reference}</span> : "—"}{c.notes && <div style={{ ...text.meta, maxWidth: 260 }}>{c.notes}</div>}</td>
                  <td style={{ padding: "7px 10px" }}>{c.incident ? <PortalLink to={`/resilience/incidents?incident=${c.incident.id}`} style={{ color: "var(--c-accentText)", textDecoration: "none" }} title={c.incident.title}>{c.incidentRef ?? c.incident.title}</PortalLink> : <span style={{ color: theme.color.faint }}>—</span>}</td>
                  <td style={{ padding: "7px 10px" }}><ClaimStatusPill status={c.status} />{c.closedAt && <div style={text.meta}>{fmtDateOnly(c.closedAt)}</div>}</td>
                  <td style={{ padding: "7px 10px", color: theme.color.muted, whiteSpace: "nowrap" }}>{money(c.amountClaimed, currency)}</td>
                  <td style={{ padding: "7px 10px", color: theme.color.muted, whiteSpace: "nowrap" }}>{money(c.amountPaid, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {viewing && !editing && (
        <ModalOverlay onClose={() => { setViewingId(null); setSynced(null); }}>
          <ModalHeader title={<span>Claim{viewing.reference ? ` ${viewing.reference}` : ""} <ClaimStatusPill status={viewing.status} /></span>} onClose={() => { setViewingId(null); setSynced(null); }} right={canEdit ? <EditButton onClick={() => setEditing({ claim: viewing })} /> : undefined} />
          <div style={{ padding: 20, display: "grid", gap: 12 }}>
            <NextSteps items={claimSteps(viewing)} action={canEdit ? <button type="button" style={secondaryBtn} onClick={() => setEditing({ claim: viewing })}>Fill these in</button> : undefined} />
            <DetailSection title="The claim">
            <DetailFields fields={[
              { label: "Insurer notified", value: fmtDateOnly(viewing.notifiedAt) },
              { label: "Status", value: CLAIM_STATUS_LABELS[viewing.status] ?? viewing.status },
              { label: "Insurer's reference", value: viewing.reference },
              { label: "Incident", value: viewing.incident ? <PortalLink to={`/resilience/incidents?incident=${viewing.incident.id}`} style={{ color: "var(--c-accentText)", textDecoration: "none" }}>{viewing.incidentRef ? `${viewing.incidentRef} ` : ""}{viewing.incident.title}</PortalLink> : null },
              ...(viewing.notification ? [{
                label: "Insurer notification",
                value: (
                  <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    The notice of the incident's insurer notification <StatusPill value={viewing.notification.status} />
                    {viewing.notification.sentAt && <span style={text.meta}>sent {fmtDateTime(viewing.notification.sentAt)}{viewing.notification.reference ? ` · ref ${viewing.notification.reference}` : ""}</span>}
                  </span>
                ),
                wide: true,
              }] : []),
              { label: "Amount claimed", value: viewing.amountClaimed == null ? null : money(viewing.amountClaimed, currency) },
              { label: "Amount paid", value: viewing.amountPaid == null ? null : money(viewing.amountPaid, currency) },
              { label: "Settled / closed on", value: viewing.closedAt ? fmtDateOnly(viewing.closedAt) : null },
              { label: "Notes", value: viewing.notes, wide: true, multiline: true },
            ]} />
            </DetailSection>
            {synced && <SyncNotice sync={synced} onDismiss={() => setSynced(null)} />}
            {canEdit && (
              <RecordActions>
                <button type="button" style={dangerBtn} onClick={() => void remove(viewing)}>Delete claim</button>
              </RecordActions>
            )}
          </div>
        </ModalOverlay>
      )}
      {/* Saving an edit returns to the claim's view (still selected underneath). */}
      {editing && <ClaimForm policyId={policyId} existing={editing.claim} onClose={() => setEditing(null)} onSaved={async (sync) => { setEditing(null); setSynced(sync && (sync.filled.length || sync.skipped.length) ? sync : null); await onChanged(); }} />}
    </div>
  );
}

type Form = { incidentId: string; notifiedAt: string; status: ClaimStatus; reference: string; amountClaimed: string; amountPaid: string; closedAt: string; notes: string };

function ClaimForm({ policyId, existing, onClose, onSaved }: { policyId: string; existing: InsuranceClaim | null; onClose: () => void; onSaved: (sync: SyncResult | null) => Promise<void> }) {
  const [f, setF] = useState<Form>(existing
    ? { incidentId: existing.incidentId ?? "", notifiedAt: toDateInput(existing.notifiedAt), status: existing.status, reference: existing.reference ?? "", amountClaimed: existing.amountClaimed?.toString() ?? "", amountPaid: existing.amountPaid?.toString() ?? "", closedAt: toDateInput(existing.closedAt), notes: existing.notes ?? "" }
    : { incidentId: "", notifiedAt: todayInput(), status: "notified", reference: "", amountClaimed: "", amountPaid: "", closedAt: "", notes: "" });
  const [incidents, setIncidents] = useState<Option[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    api.get<{ incidents: Array<{ id: string; displayId: number | null; title: string }> }>("/resilience/incidents")
      .then((d) => setIncidents(d.incidents.map((i) => ({ id: i.id, name: `${i.displayId != null ? `INC-${String(i.displayId).padStart(3, "0")} ` : ""}${i.title}` }))))
      .catch(() => setIncidents([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    const num = (v: string) => (v.trim() === "" ? null : Number(v));
    const payload = {
      incidentId: f.incidentId || null, notifiedAt: fromInput(f.notifiedAt) ?? new Date().toISOString(), status: f.status, reference: f.reference.trim() || null,
      amountClaimed: num(f.amountClaimed), amountPaid: num(f.amountPaid),
      // Left out when blank, so the server dates a paid, declined or closed claim itself.
      closedAt: f.closedAt ? fromInput(f.closedAt) : undefined, notes: f.notes.trim() || null,
    };
    try {
      // Both answer { claim, sync }: sync says what the save also recorded on the incident's insurer notification.
      const res = existing
        ? await api.put<{ sync?: SyncResult | null }>(`/resilience/insurance/${policyId}/claims/${existing.id}`, payload)
        : await api.post<{ sync?: SyncResult | null }>(`/resilience/insurance/${policyId}/claims`, payload);
      await onSaved(res.sync ?? null);
    } catch (err) { setError(apiErrorMessage(err, "Could not save the claim")); setSaving(false); }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={existing ? "Edit claim" : "Record a claim"} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Incident" span2 help="The incident the claim arises from, if it is in the incident register."><select value={f.incidentId} onChange={(e) => set("incidentId", e.target.value)} style={input}><option value="">None / not recorded</option>{incidents.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</select></Field>
        <Field label="Insurer notified *"><input type="date" required max={todayInput()} value={f.notifiedAt} onChange={(e) => set("notifiedAt", e.target.value)} style={input} /></Field>
        <Field label="Status"><select value={f.status} onChange={(e) => set("status", e.target.value as ClaimStatus)} style={input}>{CLAIM_STATUSES.map((s) => <option key={s} value={s}>{CLAIM_STATUS_LABELS[s]}</option>)}</select></Field>
        <Field label="Insurer's reference" span2><input value={f.reference} onChange={(e) => set("reference", e.target.value)} style={input} /></Field>
        <Field label="Amount claimed"><input type="number" min={0} value={f.amountClaimed} onChange={(e) => set("amountClaimed", e.target.value)} style={input} /></Field>
        <Field label="Amount paid"><input type="number" min={0} value={f.amountPaid} onChange={(e) => set("amountPaid", e.target.value)} style={input} /></Field>
        <Field label="Settled / closed on" help="Set automatically when the claim is paid, declined or closed."><input type="date" value={f.closedAt} onChange={(e) => set("closedAt", e.target.value)} style={input} /></Field>
        <div />
        <Field label="Notes" span2><textarea rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} style={textarea} placeholder="Loss adjuster, panel firm appointed, conditions the insurer raised" /></Field>
        {error && <div style={{ ...text.body, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}
